import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import { getErrorCopy } from '@/shared/copy/errors';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { iconSizes, s } from '@/theme/tokens';
import { ChartEmptyState, CHART_EMPTY_STATE_NO_DATA_LABEL } from '@/ui/composites';
import { useDeferredAsyncViewState } from '@/ui/composites/useDeferredAsyncViewState';
import { Stack } from '@/ui/layout/Stack';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Text } from '@/ui/primitives';
import { KeyboardArrowDownIcon } from '@/ui/primitives/icons';
import {
  DAILY_STATUS_BLOCK_SIZE,
  DAILY_STATUS_ROW_HEIGHT,
  HEADER_SEPARATOR_SX,
  HEADER_SUBTEXT_SX,
  HEADER_TEXT_SX,
  HISTORY_NAME_COLUMN_RESIZE_HANDLE_WIDTH_PX,
  HISTORY_PANEL_CONTENT_HEADER_HEIGHT,
  HISTORY_PANEL_CONTENT_HEADER_TOP_PADDING,
  HISTORY_STATE_GRACE_PERIOD_MS,
  ROW_NAME_TEXT_SX,
  formatHistoryHeaderDay,
  resolveHistoryBodyMinHeight,
} from './EntityDailyHistoryPanel.constants';
import { EntityDailyHistoryRows } from './EntityDailyHistoryPanelRows';
import { EntityDailyHistoryPanelSkeleton } from './EntityDailyHistoryPanelSkeleton';
import {
  entityDailyHistoryScrollContainerSx,
  entityDailyHistoryTableSx,
} from './EntityDailyHistoryPanelSurfaces';
import type {
  DailyHistoryCell,
  DailyHistorySortKey,
  EntityDailyHistoryRow,
} from './EntityDailyHistoryPanel.types';
import type {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactElement,
  ReactNode,
  RefObject,
} from 'react';

type EntityDailyHistoryPanelBodyProps = {
  title: string;
  entityLabel: string;
  entityHeaderContent: ReactNode;
  activityLabel: 'executions' | 'builds' | 'jobs' | 'runs';
  rows: EntityDailyHistoryRow[];
  loading: boolean;
  knownEmpty?: boolean;
  showErrorState: boolean;
  noRowsText: string;
  days: DailyHistoryCell[];
  visibleDayHeaderIndices: number[];
  dayHeaderGridRef: RefObject<HTMLDivElement | null>;
  nameColumnHeaderCellRef: RefObject<HTMLTableCellElement | null>;
  panelTableContainerRef: RefObject<HTMLDivElement | null>;
  nameColumnWidth: string;
  pageSize: number;
  paginatedRows: EntityDailyHistoryRow[];
  expandedRowIds: Set<string>;
  linksDisabled: boolean;
  sortKey: DailyHistorySortKey;
  sortDirection: 'asc' | 'desc';
  primarySortKey: DailyHistorySortKey;
  sortingDisabled: boolean;
  onToggleRowExpansion: (rowId: string) => void;
  onSortableHeaderChange: (sortKey: DailyHistorySortKey) => void;
  onNameColumnResizeStart: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  onNameColumnResizeKeyDown: (event: ReactKeyboardEvent<HTMLButtonElement>) => void;
};

type EntityDailyHistorySortableHeaderProps = {
  active: boolean;
  direction: 'asc' | 'desc';
  disabled: boolean;
  label: string;
  onClick: () => void;
};

const resolveHistoryPanelSurface = (theme: Parameters<typeof resolveSemanticColor>[0]): string =>
  resolveSemanticColor(theme, 'surface');

const EntityDailyHistorySortableHeader = ({
  active,
  direction,
  disabled,
  label,
  onClick,
}: EntityDailyHistorySortableHeaderProps): ReactElement => {
  if (disabled) {
    return (
      <Box
        aria-label={`${label}, sorting unavailable`}
        component="button"
        type="button"
        disabled
        sx={{
          alignItems: 'center',
          backgroundColor: 'transparent',
          border: 0,
          color: 'text.secondary',
          cursor: 'default',
          display: 'inline-flex',
          font: 'inherit',
          gap: s('sm'),
          justifyContent: 'flex-start',
          maxWidth: '100%',
          minWidth: 0,
          overflow: 'hidden',
          p: s('none'),
          textAlign: 'left',
        }}
      >
        <Text role="body" sx={[HEADER_TEXT_SX, { color: 'text.secondary' }]}>
          {label}
        </Text>
        <KeyboardArrowDownIcon
          className="EntityDailyHistorySortableHeader-icon"
          sx={{
            color: 'text.secondary',
            flexShrink: 0,
            fontSize: iconSizes.md,
            opacity: 0.38,
            transform:
              direction === 'asc'
                ? 'translateY(1px) rotate(180deg)'
                : 'translateY(1px) rotate(0deg)',
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      aria-label={
        active
          ? `${label}, sorted ${direction === 'asc' ? 'ascending' : 'descending'}`
          : `${label}, activate to sort descending`
      }
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        '&:hover .EntityDailyHistorySortableHeader-icon': {
          opacity: 1,
        },
        '&:focus-visible': {
          outline: (theme) => `2px solid ${theme.app.state.focus}`,
          outlineOffset: 2,
        },
        alignItems: 'center',
        backgroundColor: 'transparent',
        border: 0,
        color: 'inherit',
        cursor: 'pointer',
        display: 'inline-flex',
        font: 'inherit',
        gap: s('sm'),
        justifyContent: 'flex-start',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        p: s('none'),
        textAlign: 'left',
      }}
    >
      <Text role="body" sx={[HEADER_TEXT_SX, { color: 'text.secondary' }]}>
        {label}
      </Text>
      <KeyboardArrowDownIcon
        className="EntityDailyHistorySortableHeader-icon"
        sx={{
          color: 'text.secondary',
          flexShrink: 0,
          fontSize: iconSizes.md,
          opacity: active ? 1 : 0,
          transform:
            direction === 'asc' ? 'translateY(1px) rotate(180deg)' : 'translateY(1px) rotate(0deg)',
        }}
      />
    </Box>
  );
};

export const EntityDailyHistoryPanelBody = ({
  title,
  entityLabel,
  entityHeaderContent,
  activityLabel,
  rows,
  loading,
  knownEmpty = false,
  showErrorState,
  noRowsText: _noRowsText,
  days,
  visibleDayHeaderIndices,
  dayHeaderGridRef,
  nameColumnHeaderCellRef,
  panelTableContainerRef,
  nameColumnWidth,
  pageSize,
  paginatedRows,
  expandedRowIds,
  linksDisabled,
  sortKey,
  sortDirection,
  primarySortKey,
  sortingDisabled,
  onToggleRowExpansion,
  onSortableHeaderChange,
  onNameColumnResizeStart,
  onNameColumnResizeKeyDown,
}: EntityDailyHistoryPanelBodyProps): ReactElement => {
  const emptyStateMinHeight = resolveHistoryBodyMinHeight({
    rowCount: pageSize,
    rowHeight: DAILY_STATUS_ROW_HEIGHT,
    headerHeight: HISTORY_PANEL_CONTENT_HEADER_HEIGHT,
  });
  const hasEmptyState = rows.length === 0 || days.length === 0;
  const effectiveLoading = knownEmpty && hasEmptyState && !showErrorState ? false : loading;
  const viewState = useDeferredAsyncViewState({
    loading: effectiveLoading,
    hasError: showErrorState,
    isEmpty: hasEmptyState,
    delayMs: HISTORY_STATE_GRACE_PERIOD_MS,
  });
  const visibleDayHeaderLabels = visibleDayHeaderIndices.map((index) => ({
    day: days[index] as DailyHistoryCell,
    index,
  }));
  const showEmptyTable = viewState.showEmpty;
  const emptyTableBodyHeight = `${String(Math.max(1, pageSize) * DAILY_STATUS_ROW_HEIGHT)}px`;

  if (viewState.showPlaceholder || viewState.showSkeleton) {
    return (
      <Box ref={panelTableContainerRef} sx={entityDailyHistoryScrollContainerSx}>
        <Box sx={{ overflow: 'hidden' }}>
          <EntityDailyHistoryPanelSkeleton
            dayCount={days.length > 0 ? days.length : 7}
            days={days}
            entityLabel={entityLabel}
            entityHeaderContent={entityHeaderContent}
            sortDirection={sortKey === primarySortKey ? sortDirection : 'desc'}
            rowCount={pageSize}
            nameColumnWidth={nameColumnWidth}
            rowHeight={DAILY_STATUS_ROW_HEIGHT}
            blockSize={DAILY_STATUS_BLOCK_SIZE}
            headerHeight={HISTORY_PANEL_CONTENT_HEADER_HEIGHT}
            visibleDayHeaderIndices={visibleDayHeaderIndices}
            dayHeaderGridRef={dayHeaderGridRef}
            nameColumnHeaderCellRef={nameColumnHeaderCellRef}
            onNameColumnResizeStart={onNameColumnResizeStart}
            onNameColumnResizeKeyDown={onNameColumnResizeKeyDown}
          />
        </Box>
      </Box>
    );
  }

  if (viewState.showError) {
    return (
      <Box ref={panelTableContainerRef} sx={entityDailyHistoryScrollContainerSx}>
        <Box
          sx={{
            backgroundColor: resolveHistoryPanelSurface,
            px: s('xl'),
            pb: s('sm'),
            minHeight: emptyStateMinHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ChartEmptyState title={getErrorCopy('banner.historyLoadFailed')} variant="metricCard" />
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={panelTableContainerRef} sx={entityDailyHistoryScrollContainerSx}>
      <Table
        aria-label={title}
        sx={[
          entityDailyHistoryTableSx,
          {
            tableLayout: 'fixed',
            width: '100%',
            backgroundColor: resolveHistoryPanelSurface,
            '& .MuiTableBody-root > .MuiTableRow-root:first-of-type > .MuiTableCell-root': {
              borderTop: 'none',
              pt: s('sm'),
            },
            '& .MuiTableBody-root > .MuiTableRow-root:last-of-type > .MuiTableCell-root': {
              pb: s('sm'),
            },
            '& .MuiTableCell-root, & .MuiTableRow-root, & .MuiTableHead-root, & .MuiTableBody-root':
              {
                backgroundColor: resolveHistoryPanelSurface,
              },
          },
        ]}
      >
        <colgroup>
          <col width={nameColumnWidth} />
          <col width="100%" />
        </colgroup>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: resolveHistoryPanelSurface,
              height: `${HISTORY_PANEL_CONTENT_HEADER_HEIGHT}px`,
              position: 'relative',
              '&::after': buildTableDividerPseudoSx('bottom'),
            }}
          >
            <TableCell
              ref={nameColumnHeaderCellRef}
              sx={[
                {
                  backgroundColor: resolveHistoryPanelSurface,
                  width: nameColumnWidth,
                  minWidth: nameColumnWidth,
                  maxWidth: nameColumnWidth,
                  pl: s('xl'),
                  pr: s('md'),
                  pt: s('none'),
                  pb: s('none'),
                  verticalAlign: 'middle',
                  borderBottom: 'none',
                  position: 'relative',
                },
                HEADER_SEPARATOR_SX,
              ]}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap="sm"
                sx={{ minHeight: HISTORY_PANEL_CONTENT_HEADER_HEIGHT, minWidth: 0, width: '100%' }}
              >
                <EntityDailyHistorySortableHeader
                  active={sortKey === primarySortKey}
                  direction={sortKey === primarySortKey ? sortDirection : 'desc'}
                  disabled={sortingDisabled || showEmptyTable}
                  label={entityLabel}
                  onClick={() => {
                    onSortableHeaderChange(primarySortKey);
                  }}
                />
                {entityHeaderContent}
              </Stack>
              <Box
                component="button"
                type="button"
                aria-label={`Resize ${entityLabel} column`}
                onPointerDown={onNameColumnResizeStart}
                onKeyDown={onNameColumnResizeKeyDown}
                disabled={showEmptyTable}
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: `${String(HISTORY_NAME_COLUMN_RESIZE_HANDLE_WIDTH_PX / -2)}px`,
                  width: `${String(HISTORY_NAME_COLUMN_RESIZE_HANDLE_WIDTH_PX)}px`,
                  height: '100%',
                  p: s('none'),
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: showEmptyTable ? 'default' : 'col-resize',
                  zIndex: 1,
                  touchAction: 'none',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: s('sm'),
                    bottom: s('sm'),
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 0,
                    borderRadius: '999px',
                    borderLeft: '2px solid',
                    borderColor: 'divider',
                    opacity: 0,
                    transition: (muiTheme) =>
                      muiTheme.transitions.create('opacity', {
                        duration: muiTheme.transitions.duration.shortest,
                      }),
                  },
                  '&:not(:disabled):hover::after, &:not(:disabled):focus-visible::after, &:not(:disabled):active::after':
                    {
                      opacity: 1,
                    },
                  '&:focus-visible': {
                    outline: (theme) => `2px solid ${theme.app.state.focus}`,
                    outlineOffset: 2,
                  },
                }}
              />
            </TableCell>
            <TableCell
              sx={{
                backgroundColor: resolveHistoryPanelSurface,
                width: '100%',
                px: s('xl'),
                pt: HISTORY_PANEL_CONTENT_HEADER_TOP_PADDING,
                pb: s('none'),
                verticalAlign: 'top',
                borderBottom: 'none',
              }}
            >
              <Stack gap="xs" sx={{ minWidth: 0 }}>
                <Text role="body" sx={[HEADER_TEXT_SX, { color: 'text.secondary' }]}>
                  Status
                </Text>
                <Box
                  ref={dayHeaderGridRef}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${String(days.length)}, minmax(0, 1fr))`,
                    columnGap: s('md'),
                    alignItems: 'center',
                    width: '100%',
                    minWidth: 0,
                    height: '16px',
                    pb: s('none'),
                  }}
                >
                  {days.map((day, index) => {
                    const visibleLabel = visibleDayHeaderLabels.find(
                      (entry) => entry.index === index,
                    );

                    return (
                      <Box
                        key={`${day.day.toISOString()}-${String(index)}`}
                        sx={{
                          position: 'relative',
                          width: '100%',
                          minWidth: 0,
                          height: '16px',
                        }}
                      >
                        {visibleLabel ? (
                          <Text
                            role="body"
                            sx={[
                              HEADER_SUBTEXT_SX,
                              {
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                whiteSpace: 'nowrap',
                                pointerEvents: 'none',
                              },
                            ]}
                          >
                            {formatHistoryHeaderDay(visibleLabel.day.day)}
                          </Text>
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
              </Stack>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {showEmptyTable ? (
            <TableRow sx={{ height: emptyTableBodyHeight }}>
              <TableCell
                colSpan={2}
                sx={{
                  borderBottom: 'none',
                  height: emptyTableBodyHeight,
                  p: s('none'),
                }}
              >
                <ChartEmptyState title={CHART_EMPTY_STATE_NO_DATA_LABEL} variant="metricCard" />
              </TableCell>
            </TableRow>
          ) : (
            <EntityDailyHistoryRows
              rows={paginatedRows}
              expandedRowIds={expandedRowIds}
              onToggleRowExpansion={onToggleRowExpansion}
              rowHeight={DAILY_STATUS_ROW_HEIGHT}
              blockSize={DAILY_STATUS_BLOCK_SIZE}
              nameColumnWidth={nameColumnWidth}
              rowNameTextSx={ROW_NAME_TEXT_SX}
              activityLabel={activityLabel}
              linksDisabled={linksDisabled}
            />
          )}
        </TableBody>
      </Table>
    </Box>
  );
};
